import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ArrowLeft, Share2, CheckCircle, XCircle, User, Search, Pause, Play, Settings, Grid3X3, Copy, Check, RotateCcw, Upload, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input as InputUI } from '../ui/input';
import { Label } from '../ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import ShareTextDialog from '../ui/share-text-dialog';

const RaffleManagement = () => {
  const { raffleId } = useParams();
  const navigate = useNavigate();
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [activeTab, setActiveTab] = useState('available');
  const [selectedUserEntries, setSelectedUserEntries] = useState({});
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareText, setShareText] = useState('');
  const [shareTitle, setShareTitle] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editData, setEditData] = useState({
    title: '',
    unit_price: '',
    payment_alias: '',
    whatsapp_number: '',
    payment_holder_name: '',
    observations: ''
  });
  const [editPrizes, setEditPrizes] = useState([]);
  const [editLogo, setEditLogo] = useState(null);
  const [editPrizeImages, setEditPrizeImages] = useState([]);
  const [newGridType, setNewGridType] = useState('');
  const [customNumbers, setCustomNumbers] = useState('');
  const [changingGrid, setChangingGrid] = useState(false);
  const [copiedShareLink, setCopiedShareLink] = useState(false);

  const handleCopyShareLink = () => {
    const link = `${window.location.origin}/api/share/rifa/${raffleId}`;
    navigator.clipboard.writeText(link);
    setCopiedShareLink(true);
    toast.success('Link copiado - pegalo en WhatsApp');
    setTimeout(() => setCopiedShareLink(false), 2000);
  };

  useEffect(() => {
    fetchRaffle();
  }, [raffleId]);

  const fetchRaffle = async () => {
    try {
      const response = await axios.get(`${API}/raffles/${raffleId}`);
      setRaffle(response.data);
    } catch (error) {
      console.error('Error fetching raffle:', error);
      toast.error('Error al cargar rifa');
    } finally {
      setLoading(false);
    }
  };

  const handleEntryClick = (entry) => {
    const isSelected = selectedEntries.find(e => e.id === entry.id);
    if (isSelected) {
      setSelectedEntries(selectedEntries.filter(e => e.id !== entry.id));
    } else {
      setSelectedEntries([...selectedEntries, entry]);
    }
  };

  const handleSelectAllUserEntries = (userKey, entries) => {
    const entryIds = entries.map(e => e.id);
    const allSelected = entryIds.every(id => selectedEntries.find(e => e.id === id));
    
    if (allSelected) {
      // Deseleccionar todos
      setSelectedEntries(selectedEntries.filter(e => !entryIds.includes(e.id)));
    } else {
      // Seleccionar todos los que no estén seleccionados
      const newSelections = entries.filter(e => !selectedEntries.find(s => s.id === e.id));
      setSelectedEntries([...selectedEntries, ...newSelections]);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (selectedEntries.length === 0) {
      toast.error('Seleccioná al menos un número');
      return;
    }

    try {
      await axios.post(`${API}/raffles/${raffleId}/update-status`, {
        entry_ids: selectedEntries.map(e => e.id),
        new_status: newStatus
      });
      toast.success('Estado actualizado correctamente');
      setSelectedEntries([]);
      fetchRaffle();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar estado');
    }
  };

  const handleShareList = async (status) => {
    try {
      const response = await axios.get(`${API}/raffles/${raffleId}/share-text/${status}`);
      const { share_text } = response.data;

      const statusNames = {
        'available': 'Números Disponibles',
        'reserved': 'Números Reservados',
        'paid': 'Números Pagados'
      };

      setShareTitle(`Compartir ${statusNames[status]}`);
      setShareText(share_text);
      setShareDialogOpen(true);
    } catch (error) {
      console.error('Error sharing list:', error);
      toast.error('Error al generar listado');
    }
  };

  const handleToggleEditPanel = () => {
    if (!showEditPanel && raffle) {
      setEditData({
        title: raffle.title || '',
        unit_price: raffle.unit_price || '',
        payment_alias: raffle.payment_alias || '',
        whatsapp_number: raffle.whatsapp_number || '',
        payment_holder_name: raffle.payment_holder_name || '',
        observations: raffle.observations || ''
      });
      setEditPrizes([...(raffle.prizes || [''])]);
      setEditLogo(raffle.logo || null);
      setEditPrizeImages([...(raffle.prize_images || [])]);
      setNewGridType(raffle.raffle_type || 'classic');
    }
    setShowEditPanel(!showEditPanel);
  };

  const handleEditImageUpload = (e, type) => {
    const files = Array.from(e.target.files);
    const maxSize = 2 * 1024 * 1024;
    for (const file of files) {
      if (file.size > maxSize) {
        toast.error('Las imágenes deben ser menores a 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'logo') {
          setEditLogo(reader.result);
        } else {
          setEditPrizeImages(prev => [...prev, reader.result]);
        }
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleSaveEdit = async () => {
    try {
      const cleanedPrizes = editPrizes.filter(p => p.trim() !== '');
      if (cleanedPrizes.length === 0) {
        toast.error('Agregá al menos un premio');
        return;
      }

      const update = {};
      if (editData.title !== raffle.title) update.title = editData.title;
      if (parseFloat(editData.unit_price) !== raffle.unit_price) update.unit_price = parseFloat(editData.unit_price);
      if (editData.payment_alias !== raffle.payment_alias) update.payment_alias = editData.payment_alias;
      if (editData.whatsapp_number !== raffle.whatsapp_number) update.whatsapp_number = editData.whatsapp_number;
      if ((editData.payment_holder_name || '') !== (raffle.payment_holder_name || '')) update.payment_holder_name = editData.payment_holder_name || null;
      if ((editData.observations || '') !== (raffle.observations || '')) update.observations = editData.observations || null;
      if (JSON.stringify(cleanedPrizes) !== JSON.stringify(raffle.prizes)) update.prizes = cleanedPrizes;
      if ((editLogo || null) !== (raffle.logo || null)) update.logo = editLogo;
      if (JSON.stringify(editPrizeImages) !== JSON.stringify(raffle.prize_images || [])) update.prize_images = editPrizeImages;

      if (Object.keys(update).length > 0) {
        await axios.put(`${API}/raffles/${raffleId}`, update);
        toast.success('Datos actualizados');
        fetchRaffle();
      } else {
        toast.info('No hay cambios para guardar');
      }
    } catch (error) {
      toast.error('Error al guardar cambios');
    }
  };

  const handleChangeGrid = async () => {
    if (!newGridType) return;
    
    const hasBuyers = raffle.entries.some(e => e.status !== 'available');
    const msg = hasBuyers 
      ? 'Hay números vendidos/reservados. Se intentará preservarlos en la nueva grilla. Los que no coincidan se perderán. ¿Continuar?'
      : '¿Cambiar la grilla? Esto regenerará todos los números.';
    
    if (!window.confirm(msg)) return;
    
    setChangingGrid(true);
    try {
      const payload = { raffle_type: newGridType };
      if (newGridType === 'custom') {
        if (!customNumbers || parseInt(customNumbers) <= 0) {
          toast.error('Ingresá una cantidad válida de números');
          setChangingGrid(false);
          return;
        }
        payload.total_numbers = parseInt(customNumbers);
      }
      
      const response = await axios.post(`${API}/raffles/${raffleId}/change-grid`, payload);
      toast.success(response.data.message);
      fetchRaffle();
      setShowEditPanel(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar grilla');
    } finally {
      setChangingGrid(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const newStatus = !raffle.is_active;
      await axios.put(`${API}/raffles/${raffleId}`, { is_active: newStatus });
      toast.success(newStatus ? 'Rifa activada' : 'Rifa pausada');
      fetchRaffle();
    } catch (error) {
      console.error('Error toggling raffle:', error);
      toast.error('Error al cambiar estado');
    }
  };

  const handleResetParticipants = async () => {
    const confirmMsg = `¿Reiniciar todos los participantes de esta rifa?\n\n• Todos los números vuelven a "Disponible"\n• Se pierden los nombres/teléfonos asignados a los números\n• La rifa queda lista para volver a usarse desde cero\n• NO se elimina la rifa, ni sus clientes registrados en "Clientes"\n\nEsto no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const response = await axios.post(`${API}/raffles/${raffleId}/reset-participants`);
      toast.success(response.data.message || 'Rifa reiniciada');
      setSelectedEntries([]);
      fetchRaffle();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reiniciar la rifa');
    }
  };

  const handleDuplicate = async () => {
    if (!window.confirm('¿Duplicar esta rifa?\n\nSe creará una nueva rifa con el mismo título (+ "(copia)"), premios, fotos, alias y tipo. La rifa original no se modifica.')) return;
    try {
      const response = await axios.post(`${API}/raffles/${raffleId}/duplicate`);
      toast.success('Rifa duplicada. Abriendo la nueva...');
      setTimeout(() => {
        navigate(`/admin/manage/${response.data.new_raffle_id}`);
      }, 600);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al duplicar la rifa');
    }
  };

  const getEntriesByStatus = (status) => {
    if (!raffle) return [];
    let entries = raffle.entries.filter(e => e.status === status);
    
    // Aplicar filtro de búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      entries = entries.filter(entry => {
        // Buscar por nombre
        if (entry.buyer_name?.toLowerCase().includes(term)) return true;
        // Buscar por número de rifa
        if (entry.number?.toLowerCase().includes(term)) return true;
        // Buscar por teléfono
        if (entry.buyer_phone?.includes(term)) return true;
        // Buscar en display_numbers si existen
        if (entry.display_numbers?.some(n => n.toString().includes(term))) return true;
        return false;
      });
    }
    
    return entries;
  };

  const groupEntriesByUser = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      if (entry.buyer_name) {
        const key = `${entry.buyer_name}|${entry.buyer_phone}`;
        if (!grouped[key]) {
          grouped[key] = {
            buyer_name: entry.buyer_name,
            buyer_phone: entry.buyer_phone,
            entries: []
          };
        }
        grouped[key].entries.push(entry);
      }
    });
    return grouped;
  };

  const renderUserGroupedView = (entries) => {
    const grouped = groupEntriesByUser(entries);
    const userKeys = Object.keys(grouped);
    const orphanEntries = entries.filter(e => !e.buyer_name);

    if (userKeys.length === 0 && orphanEntries.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          No hay números reservados
        </div>
      );
    }

    const orphansSelected = orphanEntries.filter(e => selectedEntries.find(s => s.id === e.id));
    const allOrphansSelected = orphanEntries.length > 0 && orphansSelected.length === orphanEntries.length;

    const handleReleaseAllOrphans = async () => {
      if (orphanEntries.length === 0) return;
      if (!window.confirm(`¿Liberar ${orphanEntries.length} número${orphanEntries.length !== 1 ? 's' : ''} huérfano${orphanEntries.length !== 1 ? 's' : ''}?`)) return;
      try {
        await axios.post(`${API}/raffles/${raffleId}/update-status`, {
          entry_ids: orphanEntries.map(e => e.id),
          new_status: 'available'
        });
        toast.success(`${orphanEntries.length} número${orphanEntries.length !== 1 ? 's' : ''} liberado${orphanEntries.length !== 1 ? 's' : ''}`);
        setSelectedEntries([]);
        fetchRaffle();
      } catch (error) {
        toast.error('Error al liberar los números');
      }
    };

    return (
      <div className="space-y-4">
        {orphanEntries.length > 0 && (
          <div className="border-2 border-red-200 bg-red-50 rounded-lg p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div>
                <h4 className="font-semibold text-red-800 flex items-center gap-2">
                  <XCircle size={18} />
                  Números reservados sin comprador ({orphanEntries.length})
                </h4>
                <p className="text-xs text-red-700 mt-0.5">
                  Estos números están reservados pero no tienen nombre asignado. Liberalos para que vuelvan a estar disponibles.
                </p>
              </div>
              <Button
                onClick={handleReleaseAllOrphans}
                className="bg-red-600 hover:bg-red-700 text-white text-xs"
                size="sm"
                data-testid="release-all-orphans"
              >
                <XCircle size={14} className="mr-1" />
                Liberar todos
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {orphanEntries.map(entry => {
                const isSelected = selectedEntries.find(e => e.id === entry.id);
                return (
                  <button
                    key={entry.id}
                    onClick={() => handleEntryClick(entry)}
                    className={`px-3 py-1.5 rounded-md font-semibold text-sm transition-all ${
                      isSelected
                        ? 'bg-blue-500 text-white ring-2 ring-blue-600'
                        : 'bg-amber-500 text-white hover:bg-amber-600'
                    }`}
                    data-testid={`orphan-reserved-${entry.number}`}
                  >
                    {entry.number}
                    {entry.display_numbers && (
                      <span className="text-xs ml-1 opacity-75">
                        ({entry.display_numbers.join('-')})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {orphansSelected.length > 0 && (
              <div className="mt-3 flex gap-2 pt-3 border-t border-red-200">
                <Button
                  size="sm"
                  onClick={() => {
                    setSelectedEntries(orphansSelected);
                    handleUpdateStatus('available');
                  }}
                  variant="outline"
                  className="text-xs"
                >
                  <XCircle size={14} className="mr-1" />
                  Liberar seleccionados ({orphansSelected.length})
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    const entryIds = orphanEntries.map(e => e.id);
                    if (allOrphansSelected) {
                      setSelectedEntries(selectedEntries.filter(e => !entryIds.includes(e.id)));
                    } else {
                      const newSel = orphanEntries.filter(e => !selectedEntries.find(s => s.id === e.id));
                      setSelectedEntries([...selectedEntries, ...newSel]);
                    }
                  }}
                  variant="ghost"
                  className="text-xs"
                >
                  {allOrphansSelected ? 'Deseleccionar' : 'Seleccionar todos'}
                </Button>
              </div>
            )}
          </div>
        )}

        {userKeys.length > 0 && (
          <Accordion type="single" collapsible className="space-y-3">
            {userKeys.map((userKey) => {
              const userData = grouped[userKey];
              const userEntries = userData.entries;
              const allSelected = userEntries.every(e => selectedEntries.find(s => s.id === e.id));

              return (
                <AccordionItem key={userKey} value={userKey} className="border rounded-lg">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <User className="text-purple-600" size={20} />
                        <div className="text-left">
                          <p className="font-semibold">{userData.buyer_name}</p>
                          <p className="text-sm text-gray-500">{userData.buyer_phone}</p>
                        </div>
                      </div>
                      <Badge variant="secondary">
                        {userEntries.length} número{userEntries.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="px-4 pb-4 space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {userEntries.map((entry) => {
                          const isSelected = selectedEntries.find(e => e.id === entry.id);
                          return (
                            <button
                              key={entry.id}
                              onClick={() => handleEntryClick(entry)}
                              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                                isSelected
                                  ? 'bg-blue-500 text-white ring-2 ring-blue-600'
                                  : entry.status === 'reserved'
                                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                                  : 'bg-purple-500 text-white hover:bg-purple-600'
                              }`}
                            >
                              {entry.number}
                              {entry.display_numbers && (
                                <span className="text-xs ml-1 opacity-75">
                                  ({entry.display_numbers.join('-')})
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      <div className="flex flex-wrap gap-2 pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSelectAllUserEntries(userKey, userEntries)}
                          className="text-xs px-2"
                        >
                          {allSelected ? 'Deselec.' : 'Selec. Todo'}
                        </Button>
                        {selectedEntries.length > 0 && selectedEntries.some(e => userEntries.find(ue => ue.id === e.id)) && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => {
                                const userEntryIds = userEntries.map(e => e.id);
                                const entriesToUpdate = selectedEntries.filter(e => userEntryIds.includes(e.id));
                                if (entriesToUpdate.length > 0) {
                                  setSelectedEntries(entriesToUpdate);
                                  handleUpdateStatus('paid');
                                }
                              }}
                              className="text-xs px-2 bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Pagado
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                const userEntryIds = userEntries.map(e => e.id);
                                const entriesToUpdate = selectedEntries.filter(e => userEntryIds.includes(e.id));
                                if (entriesToUpdate.length > 0) {
                                  setSelectedEntries(entriesToUpdate);
                                  handleUpdateStatus('available');
                                }
                              }}
                              variant="outline"
                              className="text-xs px-2"
                            >
                              <XCircle size={14} className="mr-1" />
                              Liberar
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>
    );
  };

  const renderEntries = (entries, canSelect = false) => {
    if (entries.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          No hay números con este estado
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-5 md:grid-cols-10 lg:grid-cols-15 gap-2">
          {entries.map((entry) => {
            const isSelected = selectedEntries.find(e => e.id === entry.id);
            const statusClass =
              entry.status === 'available'
                ? 'bg-green-500 text-white'
                : entry.status === 'reserved'
                ? 'bg-amber-500 text-white'
                : 'bg-purple-500 text-white';

            return (
              <button
                key={entry.id}
                onClick={() => canSelect && handleEntryClick(entry)}
                className={`${statusClass} ${
                  isSelected ? 'ring-4 ring-blue-500' : ''
                } p-3 rounded-lg font-semibold text-sm transition-all ${
                  canSelect ? 'cursor-pointer hover:scale-105' : 'cursor-default'
                }`}
                data-testid={`entry-${entry.number}-${entry.status}`}
                title={entry.display_numbers ? entry.display_numbers.join(', ') : entry.number}
              >
                {entry.number}
              </button>
            );
          })}
        </div>

        {entries.some(e => e.buyer_name) && (
          <div className="mt-6 border-t pt-4">
            <h4 className="font-semibold mb-3">Detalles de Compradores:</h4>
            <div className="space-y-2">
              {entries
                .filter(e => e.buyer_name)
                .map((entry) => (
                  <div
                    key={entry.id}
                    className="bg-gray-50 p-3 rounded-lg flex items-center justify-between"
                  >
                    <div>
                      <Badge variant="outline" className="mr-2">
                        {entry.number}
                      </Badge>
                      <span className="font-semibold">{entry.buyer_name}</span>
                      <span className="text-gray-600 ml-2">• {entry.buyer_phone}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-xl">Cargando...</div>
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className="text-center py-12">
        <div className="text-xl">Rifa no encontrada</div>
      </div>
    );
  }

  const availableEntries = getEntriesByStatus('available');
  const reservedEntries = getEntriesByStatus('reserved');
  const paidEntries = getEntriesByStatus('paid');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          onClick={() => navigate('/admin')}
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2" size={20} />
          Volver
        </Button>
        <div className="flex gap-2">
          <Button
            onClick={handleToggleEditPanel}
            variant={showEditPanel ? 'default' : 'outline'}
            data-testid="edit-raffle-btn"
          >
            <Settings className="mr-2" size={18} />
            {showEditPanel ? 'Cerrar Edición' : 'Editar'}
          </Button>
          <Button
            onClick={handleToggleActive}
            className={raffle.is_active ? 'bg-amber-500 hover:bg-amber-600' : 'bg-green-600 hover:bg-green-700'}
            data-testid="toggle-active-btn"
          >
            {raffle.is_active ? (
              <>
                <Pause className="mr-2" size={18} />
                Pausar
              </>
            ) : (
              <>
                <Play className="mr-2" size={18} />
                Activar
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Link para compartir en WhatsApp */}
      <Card className="bg-green-50 border-green-200">
        <CardContent className="pt-6">
          <h3 className="font-semibold mb-1">Link para compartir en WhatsApp:</h3>
          <p className="text-xs text-gray-600 mb-3">Este link muestra la imagen y datos de la rifa al pegarlo en WhatsApp.</p>
          <div className="flex gap-2">
            <code className="flex-1 bg-white p-3 rounded border text-sm break-all" data-testid="raffle-share-link">
              {`${window.location.origin}/api/share/rifa/${raffleId}`}
            </code>
            <Button onClick={handleCopyShareLink} className="bg-green-600 hover:bg-green-700" data-testid="copy-raffle-share-link">
              {copiedShareLink ? <Check size={18} /> : <Copy size={18} />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Zona de acciones avanzadas */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <h3 className="font-semibold text-amber-900 flex items-center gap-2 mb-1">
                <Copy size={18} />
                Duplicar rifa
              </h3>
              <p className="text-xs text-amber-800">
                Crea una copia independiente con el mismo título, premios, fotos, alias y tipo. Ideal para lanzar otra edición sin perder la actual.
              </p>
            </div>
            <Button
              onClick={handleDuplicate}
              variant="outline"
              className="bg-white border-amber-400 text-amber-900 hover:bg-amber-100"
              data-testid="duplicate-raffle"
            >
              <Copy size={16} className="mr-2" />
              Duplicar rifa
            </Button>
          </div>
          <div className="border-t border-amber-200" />
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <h3 className="font-semibold text-amber-900 flex items-center gap-2 mb-1">
                <RotateCcw size={18} />
                Reiniciar rifa
              </h3>
              <p className="text-xs text-amber-800">
                Pasa todos los números a "Disponible" para reusar esta misma rifa (mismo título, premios, fotos). Tus clientes registrados no se borran.
              </p>
            </div>
            <Button
              onClick={handleResetParticipants}
              variant="outline"
              className="bg-white border-amber-400 text-amber-900 hover:bg-amber-100"
              data-testid="reset-raffle-participants"
            >
              <RotateCcw size={16} className="mr-2" />
              Reiniciar participantes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Panel de Edición */}
      {showEditPanel && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Settings size={20} />
              Editar Configuración
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Datos generales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Título</Label>
                <Input
                  value={editData.title}
                  onChange={(e) => setEditData({...editData, title: e.target.value})}
                  data-testid="edit-title"
                />
              </div>
              <div>
                <Label>Valor número/fila</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={editData.unit_price}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                    setEditData({...editData, unit_price: v});
                  }}
                  data-testid="edit-price"
                />
              </div>
              <div>
                <Label>Alias de pago</Label>
                <Input
                  value={editData.payment_alias}
                  onChange={(e) => setEditData({...editData, payment_alias: e.target.value})}
                  data-testid="edit-alias"
                />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={editData.whatsapp_number}
                  onChange={(e) => setEditData({...editData, whatsapp_number: e.target.value})}
                  data-testid="edit-whatsapp"
                />
              </div>
              <div className="md:col-span-2">
                <Label>A nombre de (titular del alias)</Label>
                <Input
                  value={editData.payment_holder_name}
                  onChange={(e) => setEditData({...editData, payment_holder_name: e.target.value})}
                  placeholder="Ej: Juan Perez"
                  data-testid="edit-payment-holder"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Observaciones</Label>
                <textarea
                  value={editData.observations}
                  onChange={(e) => setEditData({...editData, observations: e.target.value})}
                  placeholder="Aclaraciones para los participantes"
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="edit-observations"
                />
              </div>
            </div>

            {/* Premios (títulos) */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Premios</Label>
              <div className="space-y-2 mt-2">
                {editPrizes.map((prize, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={prize}
                      onChange={(e) => {
                        const np = [...editPrizes];
                        np[idx] = e.target.value;
                        setEditPrizes(np);
                      }}
                      placeholder={`Premio ${idx + 1}`}
                      data-testid={`edit-prize-${idx}`}
                    />
                    {editPrizes.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditPrizes(editPrizes.filter((_, i) => i !== idx))}
                        className="text-red-600 shrink-0"
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditPrizes([...editPrizes, ''])}
                  data-testid="edit-add-prize"
                >
                  <Plus size={14} className="mr-1" />
                  Agregar premio
                </Button>
              </div>
            </div>

            {/* Logo */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Logo</Label>
              <div className="mt-2">
                {editLogo ? (
                  <div className="relative inline-block">
                    <img src={editLogo} alt="Logo" className="w-32 h-32 object-contain border rounded bg-white" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => setEditLogo(null)}
                      className="absolute -top-2 -right-2"
                      data-testid="edit-remove-logo"
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer inline-block">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-500 transition-colors">
                      <Upload className="mx-auto mb-1" size={24} />
                      <p className="text-xs text-gray-600">Subir logo</p>
                    </div>
                    <input type="file" accept="image/*" onChange={(e) => handleEditImageUpload(e, 'logo')} className="hidden" data-testid="edit-logo-input" />
                  </label>
                )}
              </div>
            </div>

            {/* Imágenes de premios */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Imágenes de premios</Label>
              <div className="mt-2 flex flex-wrap gap-3">
                {editPrizeImages.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img src={img} alt={`Premio ${idx + 1}`} className="w-24 h-24 object-cover border rounded" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => setEditPrizeImages(editPrizeImages.filter((_, i) => i !== idx))}
                      className="absolute -top-2 -right-2"
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
                <label className="cursor-pointer">
                  <div className="w-24 h-24 border-2 border-dashed rounded flex items-center justify-center hover:bg-gray-50 transition-colors">
                    <Plus size={24} className="text-gray-400" />
                  </div>
                  <input type="file" accept="image/*" multiple onChange={(e) => handleEditImageUpload(e, 'prizes')} className="hidden" data-testid="edit-prize-images-input" />
                </label>
              </div>
            </div>

            <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-btn">
              Guardar Cambios
            </Button>

            {/* Cambiar Grilla */}
            <div className="border-t pt-4">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                <Grid3X3 size={18} />
                Cambiar Tipo de Grilla
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                Grilla actual: <strong>{raffle.raffle_type === 'grid_100' ? '00-99 (100)' : raffle.raffle_type === 'classic' ? '000-999 (1000)' : raffle.raffle_type === 'grid_10000' ? '0000-9999 (10000)' : raffle.raffle_type === 'lines' ? '200 Líneas x 5' : raffle.raffle_type === 'lines_125' ? '125 Líneas x 8' : raffle.raffle_type === 'lines_250' ? '250 filas Manu' : raffle.raffle_type === 'lines_250_osvaldo' ? '250 Líneas Osvaldo' : raffle.raffle_type === 'lines_50' ? '50 Líneas x 2' : raffle.raffle_type === 'lines_500' ? '500 Líneas x 2' : raffle.raffle_type === 'lines_20' ? '20 Líneas x 5' : `Personalizada (${raffle.total_numbers})`}</strong> — {raffle.entries?.length || 0} números
              </p>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="min-w-[200px]">
                  <Label>Nuevo tipo de grilla</Label>
                  <Select value={newGridType} onValueChange={setNewGridType}>
                    <SelectTrigger data-testid="grid-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grid_100">00-99 (100 números)</SelectItem>
                      <SelectItem value="classic">000-999 (1000 números)</SelectItem>
                      <SelectItem value="grid_10000">0000-9999 (10000 números)</SelectItem>
                      <SelectItem value="lines">200 Líneas x 5</SelectItem>
                      <SelectItem value="lines_125">125 Líneas x 8</SelectItem>
                      <SelectItem value="lines_250">250 filas Manu</SelectItem>
                      <SelectItem value="lines_250_osvaldo">250 Líneas Osvaldo</SelectItem>
                      <SelectItem value="lines_20">20 Líneas x 5 (00-99)</SelectItem>
                      <SelectItem value="lines_50">50 Líneas x 2</SelectItem>
                      <SelectItem value="lines_500">500 Líneas x 2</SelectItem>
                      <SelectItem value="custom">Personalizada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newGridType === 'custom' && (
                  <div className="min-w-[150px]">
                    <Label>Cantidad de números</Label>
                    <Input
                      type="number"
                      min="1"
                      value={customNumbers}
                      onChange={(e) => setCustomNumbers(e.target.value)}
                      placeholder="Ej: 500"
                      data-testid="custom-numbers-input"
                    />
                  </div>
                )}
                <Button
                  onClick={handleChangeGrid}
                  disabled={changingGrid || newGridType === raffle.raffle_type}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="change-grid-btn"
                >
                  {changingGrid ? 'Cambiando...' : 'Cambiar Grilla'}
                </Button>
              </div>
              {raffle.entries?.some(e => e.status !== 'available') && (
                <p className="text-sm text-amber-700 mt-2 bg-amber-50 p-2 rounded">
                  Hay números vendidos/reservados. Al cambiar la grilla se intentará preservarlos donde coincidan los números.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-3xl" data-testid="raffle-management-title">
            Gestionar: {raffle.title}
          </CardTitle>
          <div className="flex gap-2 mt-2">
            <Badge variant="secondary">
              {raffle.raffle_type === 'grid_100'
                ? '00-99'
                : raffle.raffle_type === 'classic'
                ? '000-999'
                : raffle.raffle_type === 'grid_10000'
                ? '0000-9999'
                : raffle.raffle_type === 'lines'
                ? '200 Líneas x 5'
                : raffle.raffle_type === 'lines_125'
                ? '125 Líneas x 8'
                : raffle.raffle_type === 'lines_250'
                ? '250 filas Manu'
                : raffle.raffle_type === 'lines_250_osvaldo'
                ? '250 Líneas Osvaldo'
                : raffle.raffle_type === 'lines_50'
                ? '50 Líneas x 2'
                : raffle.raffle_type === 'lines_500'
                ? '500 Líneas x 2'
                : raffle.raffle_type === 'lines_20'
                ? '20 Líneas x 5'
                : `Personalizada (${raffle.total_numbers})`}
            </Badge>
            <Badge variant={raffle.is_active ? 'default' : 'secondary'}>
              {raffle.is_active ? 'Activa' : 'Inactiva'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Disponibles</p>
                  <p className="text-3xl font-bold text-green-600">{availableEntries.length}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Reservados</p>
                  <p className="text-3xl font-bold text-amber-600">{reservedEntries.length}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Pagados</p>
                  <p className="text-3xl font-bold text-purple-600">{paidEntries.length}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            {/* Buscador arriba */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Buscar por nombre, número o teléfono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full md:w-80"
                />
              </div>
            </div>
            
            {/* Pestañas */}
            <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 mb-4 h-auto gap-1">
              <TabsTrigger value="available" data-testid="tab-available" className="data-[state=active]:bg-green-500 data-[state=active]:text-white py-2.5">
                <span className="w-3 h-3 rounded-full bg-green-500 mr-2 inline-block"></span>
                Disponibles ({availableEntries.length})
              </TabsTrigger>
              <TabsTrigger value="reserved" data-testid="tab-reserved" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white py-2.5">
                <span className="w-3 h-3 rounded-full bg-amber-500 mr-2 inline-block"></span>
                Reservados ({reservedEntries.length})
              </TabsTrigger>
              <TabsTrigger value="paid" data-testid="tab-paid" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white py-2.5">
                <span className="w-3 h-3 rounded-full bg-purple-500 mr-2 inline-block"></span>
                Pagados ({paidEntries.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="available" className="space-y-4">
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleShareList('available')}
                  className="bg-green-50 hover:bg-green-100"
                  data-testid="share-available"
                >
                  <Share2 className="mr-2" size={18} />
                  Compartir Disponibles por WhatsApp
                </Button>
              </div>
              {renderEntries(availableEntries, false)}
            </TabsContent>

            <TabsContent value="reserved" className="space-y-4">
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleShareList('reserved')}
                  className="bg-amber-50 hover:bg-amber-100"
                  data-testid="share-reserved"
                >
                  <Share2 className="mr-2" size={18} />
                  Compartir Reservados
                </Button>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Vista agrupada por usuario:</strong> Click en cada usuario para ver sus números. 
                  Podés seleccionarlos y marcarlos como pagados o liberarlos.
                </p>
              </div>
              {renderUserGroupedView(reservedEntries)}
            </TabsContent>

            <TabsContent value="paid" className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  {selectedEntries.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => handleUpdateStatus('reserved')}
                      className="bg-amber-50 hover:bg-amber-100"
                      data-testid="mark-as-reserved"
                    >
                      <XCircle className="mr-2" size={18} />
                      Volver a Reservado ({selectedEntries.length})
                    </Button>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => handleShareList('paid')}
                  className="bg-purple-50 hover:bg-purple-100"
                  data-testid="share-paid"
                >
                  <Share2 className="mr-2" size={18} />
                  Compartir Pagados
                </Button>
              </div>
              <p className="text-sm text-gray-600">
                Click en los números para seleccionarlos y cambiar su estado
              </p>
              {renderEntries(paidEntries, true)}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <ShareTextDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        title={shareTitle}
        text={shareText}
      />
    </div>
  );
};

export default RaffleManagement;
